import * as React from 'react';
import * as _ from 'lodash';
import * as styles from '../../help/help.module.scss';
import Tree, { TreeData, RenderItemParams, mutateTree } from '@atlaskit/tree';
import HelpDocument from '../../../models/helpDocument';
import StyledCheckbox from '../../common/checkbox';
import useHelp from '../../../hooks/useHelp';
import Scrollbars from 'react-custom-scrollbars';
import TertiaryButton from '../../common/tertiary-button';
import { useParams } from 'react-router-dom';
import useHelpDocuments from '../../../hooks/useHelpDocuments';
import SecondaryButton from '../../common/secondary-button';

interface Props {
  globalHelpDocuments: TreeData;
  mappedDocuments: HelpDocument[];
  closeModal: () => void;
}
const ClientMapModalContent = ({ globalHelpDocuments, mappedDocuments, closeModal }: Props) => {
  const { clientId } = useParams();
  const { fetchHelpDocumentsWithRevisions, fetchGlobalHelpDocuments, dispatch } = useHelpDocuments();
  const [mappedGlobalDocuments, setMappedGlobalDocuments] = React.useState<HelpDocument[]>(mappedDocuments);
  const { documentsWithoutRoot, findChildren, mapGlobalDocumentsToClients } = useHelp();

  const handleMapping = (documentId: number, toCheck: boolean) => {
    const selectedDocument = documentsWithoutRoot(globalHelpDocuments).find(
      doc => doc.data.snippetGroupId === documentId
    );
    const documentsToAlter = findChildren(documentId, [...selectedDocument.children] as number[], globalHelpDocuments);

    setMappedGlobalDocuments(prevState => {
      const nextState = _.cloneDeep(prevState);
      const mappedGlobalDocumentsIds = nextState.map(doc => doc.snippetGroupId);

      documentsToAlter.forEach(doc => {
        const checked = mappedGlobalDocuments.map(doc => doc.snippetGroupId).includes(doc);
        const alter = checked !== toCheck;
        if (alter) {
          if (mappedGlobalDocumentsIds.includes(doc)) {
            const docIndex = nextState.map(i => i.snippetGroupId).indexOf(doc);
            if (docIndex > -1) {
              nextState.splice(docIndex, 1);
            }
          } else {
            nextState.push(globalHelpDocuments.items[`${doc}`].data);
          }
        }
      });
      if (!_.isEqual(prevState, nextState)) {
        return nextState;
      }
      return prevState;
    });
  };

  const selectAll = () => {
    setMappedGlobalDocuments(documentsWithoutRoot(globalHelpDocuments).map(doc => doc.data));
  };

  const saveMapping = () => {
    const snippetGroupIds = mappedGlobalDocuments.map(doc => doc.snippetGroupId);

    closeModal();
    dispatch({ loading: true });
    return mapGlobalDocumentsToClients(Number(clientId), snippetGroupIds)
      .then(() => Promise.all([fetchHelpDocumentsWithRevisions(true), fetchGlobalHelpDocuments(true, true)]))
      .then(() => dispatch({ loading: false }))
      .catch(err => dispatch({ loading: false, errorMessage: err.responseJSON.message }));
  };

  const renderItem = ({ item, provided, onExpand }: RenderItemParams) => {
    const checked = mappedGlobalDocuments.map(doc => doc.snippetGroupId).includes(Number(item.id));
    return (
      <li ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
        <StyledCheckbox
          checked={checked}
          tabIndex={-1}
          disableRipple
          onClick={() => {
            handleMapping(Number(item.id), !checked);
          }}
        />
        <span>{item.data.title}</span>
      </li>
    );
  };

  return (
    <div className={styles.globalTree}>
      <ul className={styles.tree}>
        <Scrollbars autoHide={true} className={styles.scroll}>
          <Tree
            tree={globalHelpDocuments}
            onExpand={handleMapping as any}
            renderItem={item => renderItem(item)}
            offsetPerLevel={20}
            isNestingEnabled
          />
        </Scrollbars>
        <TertiaryButton text="Select All" handleClick={selectAll} />
      </ul>
      <SecondaryButton text="Save" handleClick={saveMapping} />
    </div>
  );
};

export default ClientMapModalContent;
